/**
 * Shared chart palette for the CMX Insights Automation Dashboard.
 *
 * Data colors (`series.*`, `rateLine`, `donutPalette`) are Tableau-aligned and
 * MUST stay locked — they match the existing Tableau dashboard so users see
 * visual continuity when moving between surfaces. Do NOT swap for CMX purple/coral.
 *
 * Brand chrome colors (`delta.*`, `text.*`) follow the CodaMetrix design system.
 */
export const chartTokens = {
  series: {
    /** Tableau-aligned muted teal — used for Automated bars and fills. */
    automated: '#4A7888',
    /** Tableau-aligned dusty clay/pink — used for Manual bars and fills. */
    manual: '#C99988',
    /** Legacy accent aliases kept for backwards compat with non-dashboard uses. */
    accentBlue: '#4A7888',
    accentCream: '#C99988',
  },
  /** Near-black solid rate line — Tableau-aligned, NOT CMX violet. */
  rateLine: '#231F20',
  axis: {
    text: '#999999',
    grid: '#EDE7F2',
  },
  surface: '#ffffff',
  /**
   * Delta arrow tints for InsightsPanel KPI rows.
   * `positive` is green (good), `negative` is red (bad), `flat` is muted.
   * Whether "up" is positive or negative is decided per row (Manual going up = bad).
   */
  delta: {
    positive: '#3a9e6f',
    negative: '#d94040',
    flat: '#999999',
  },
  /** Text shades used inside InsightsPanel. */
  text: {
    primary: '#231F20',
    secondary: '#595959',
    muted: '#7A6E84',
    faint: '#999999',
    label: '#595959',
    value: '#231F20',
    divider: '#F0EBF5',
  },
  /**
   * Eight-segment Tableau-aligned donut palette (design spec §7).
   */
  donutPalette: [
    '#4A7888', // Automated · High Confidence
    '#C99988', // Manual · Other
    '#D9A5B0', // Manual · Rule (Customer)
    '#5C8E96', // Manual · Low Confidence
    '#A8C5C9', // Manual · Product Opp.
    '#E8B999', // Manual · Error
    '#EBC9B7', // Manual · Case Quality
    '#8C5F66', // Manual · Compliance
  ] as readonly string[],
  /**
   * Legend dot colors for the automation results donut. The first four cover
   * the subcategories the POC surfaced; the remaining four use the rest of
   * donutPalette and cover the subcategories the analytics service added.
   */
  legendDot: {
    autoHigh: '#4A7888',
    autoRule: '#5C8E96',
    manualLow: '#C99988',
    manualRule: '#D9A5B0',
    manualIneligible: '#A8C5C9',
    manualNotValid: '#E8B999',
    manualOther: '#EBC9B7',
    uncategorized: '#8C5F66',
  },
} as const;

export type ChartTokens = typeof chartTokens;
